import { Row, Col, Badge } from "react-bootstrap";
import { ProductImageURL } from "components/custom/category/listings/ProductImage";
import Tags from "components/core/blocks/Tags";

import { formatCurrency } from "utils/formatNumber";
import ExternalProductLink from "components/custom/merchandise/ExternalProductLink";
import CustomTag from "components/core/blocks/CustomTag";
import { useAppSelector } from "../../../redux/hooks";

const buyboxReasons = {
  DD: "Delivery Date",
  CP: "Competitive Price",
  HP: "High Price",
  OOS: "Out of Stock",
  FM: "Not Features Merchant",
  UNK: "Unknown",
  REF: "Reference Price",
};

interface Props {
  product: {
    product_image_url: string | null;
    product_title: string;
    asin: string;
    sku: string;
    tags: string[] | null;
    eligibility_status: "ELIGIBLE" | "INELIGIBLE" | "ELIGIBLE_WITH_WARNING" | null;
    buy_box: boolean;
    buy_box_reason: keyof typeof buyboxReasons | null;
  };
  inventoryBadgeText?: string;
  salesLabel?: string;
  pricingInfo?: { amazonStatus: string | null; productPrice: number };
  updateTags: (tags: string[]) => void;
}

export function ProductSummaryHeader({ product, inventoryBadgeText, salesLabel, pricingInfo, updateTags }: Props) {
  const marketPlace = useAppSelector((state) => state.marketPlace);
  return (
    <div style={{ display: "flex" }}>
      <div style={{ width: "120px", paddingRight: "20px" }}>
        <ProductImageURL url={product.product_image_url} style={{ borderRadius: 15 }} width={100} height={100} />
      </div>
      <div>
        <Row>
          <Col xs={12}>
            <h3
              style={{
                marginBottom: "0.2rem",
                marginTop: "0",
                fontSize: 22,
              }}
            >
              {product.product_title}
            </h3>
          </Col>
          <Col xs={12}>
            {product.asin} ({product.sku}) <ExternalProductLink asin={product.asin} />
          </Col>
          <Col xs={12}>
            <Row className="pt-3">
              <Col>
                <div className="pe-5 text-center">
                  <h6 style={{ fontWeight: 600 }} className="fs-standard text-uppercase pt-1 text-nowrap">
                    Inventory
                  </h6>
                  <Badge
                    className="badge bg-trellis-purple"
                    style={{
                      marginRight: "0.5rem",
                      backgroundColor: "#c43066",
                    }}
                    pill
                  >
                    {inventoryBadgeText ? inventoryBadgeText : "unknown"}
                  </Badge>
                </div>
              </Col>
              {salesLabel && (
                <Col>
                  <div className="pe-5 text-center">
                    <h6 style={{ fontWeight: 600 }} className="fs-standard text-uppercase pt-1 text-nowrap">
                      Life Cycle
                    </h6>
                    <Badge className="badge bg-trellis-fuchsia" pill>
                      {salesLabel}
                    </Badge>
                  </div>
                </Col>
              )}
              {pricingInfo && (
                <Col>
                  <div className="pe-5 text-center">
                    <h6 style={{ fontWeight: 600 }} className="fs-standard text-uppercase pt-1 text-nowrap">
                      Listing Status
                    </h6>
                    <Badge className="badge bg-trellis-fuchsia" pill>
                      {pricingInfo.amazonStatus ?? "unknown"}
                    </Badge>
                  </div>
                </Col>
              )}
              {pricingInfo?.productPrice ?
                <Col>
                  <div className="pe-5 text-center">
                    <h6 style={{ fontWeight: 600 }} className="fs-standard text-uppercase pt-1 pe-3 text-nowrap">
                      Price
                    </h6>

                    <Badge
                      className={`badge bg-trellis-lightgrey text-end`}
                      style={{
                        borderRadius: "5px",
                      }}
                    >
                      {formatCurrency(pricingInfo.productPrice, marketPlace.marketPlace)}
                    </Badge>
                  </div>
                </Col>
              : null}
              {product.eligibility_status && (
                <Col>
                  <div className="pe-5 text-center">
                    <h6 style={{ fontWeight: 600 }} className="fs-standard text-uppercase pt-1 pe-3 text-nowrap">
                      Buy Box
                    </h6>

                    <Badge
                      className={`badge  bg-trellis-fuchsia rounded-pill text-end`}
                      style={{
                        borderRadius: "5px",
                      }}
                    >
                      {product.buy_box ? "Yes" : buyboxReasons[product.buy_box_reason ?? "UNK"]}
                    </Badge>
                  </div>
                </Col>
              )}

              <Col>
                <div className="pe-5 text-center">
                  <Tags
                    filledTags={product.tags ?? []}
                    saveFilledTags={(filledTags) => updateTags([...new Set(filledTags)])}
                    name={`Add Product Tags`}
                    hideTagsField={true}
                    hideEditButton={true}
                    altButton={
                      <span className="d-flex align-items-center">
                        <h6 style={{ fontWeight: 600 }} className="fs-standard text-uppercase pt-1 pe-2 text-nowrap">
                          Tags
                        </h6>
                        <i
                          style={{
                            fontSize: "15px",
                            opacity: ".8",
                            cursor: "pointer",
                          }}
                          className="fa fa-plus"
                        />
                      </span>
                    }
                    altButtonContainerClasses={"mx-auto"}
                  />

                  <div className="d-flex pt-1 justify-content-center gap-2">
                    {product.tags?.map((tag) => (
                      <CustomTag
                        tag={tag}
                        key={tag}
                        onUpdate={(tag) => {
                          updateTags(
                            product.tags?.filter((tagToDelete) => tag.toLowerCase() !== tagToDelete.toLowerCase()) ??
                              [],
                          );
                        }}
                      />
                    ))}
                  </div>
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
      </div>
    </div>
  );
}
